
import Link from '@/components/compat/Link';
import { ArrowRight } from 'lucide-react';
import { GsImage, XatsPriceValue, cn } from '@/components/help-center/latest-power-shared';
import { AceBadgeRow } from '@/components/help-center/aces/AceBadge';
import { ACE_ICON_SHELL_STYLE, formatAceDisplayName } from '@/components/help-center/aces/ace-shared';
import PowerDarkCardShell from '@/components/help-center/powers/PowerDarkCardShell';
import { getAceBadges, type AceInfo } from '@/lib/aces-data';
import { stripHtmlToText } from '@/lib/cms-rich-helpers';

type AceCardProps = {
  ace: AceInfo;
  className?: string;
};

export function AceCard({ ace, className }: AceCardProps) {
  const badges = getAceBadges(ace);
  const displayName = formatAceDisplayName(ace.slug);

  return (
    <Link
      href={`/aces/${ace.slug}`}
      className={cn('group block h-full text-blue-50 no-underline', className)}
    >
      <PowerDarkCardShell
        className={cn(
          'h-full ring-amber-400/20 transition-colors duration-300',
          'hover:border-amber-400/35 hover:ring-amber-400/30 hover:shadow-blue-950/50'
        )}
        innerClassName="flex h-full flex-col"
      >
        {/* Faixa superior — forma distinta dos cards de powers */}
        <div className="relative flex h-32 items-center justify-center overflow-hidden border-b border-white/[0.06]">
          <div
            className="pointer-events-none absolute inset-0 opacity-90"
            style={{
              background: `radial-gradient(circle at 50% 120%, ${ace.visual.glow} 0%, transparent 62%)`,
            }}
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-0 bg-gradient-to-b from-amber-500/10 via-transparent to-[#030023]/40"
            aria-hidden
          />

          <div
            className="relative flex h-16 w-16 items-center justify-center rounded-2xl"
            style={ACE_ICON_SHELL_STYLE}
          >
            <GsImage
              code={ace.smileyCode}
              className="flex h-full w-full items-center justify-center"
              imgClassName="max-h-[52px] max-w-[52px] object-contain"
            />
          </div>
        </div>

        <div className="flex flex-1 flex-col px-4 pb-4 pt-4">
          <div className="mb-2 flex items-start justify-between gap-2">
            <h3
              className="font-poppins text-lg font-semibold leading-tight tracking-tight text-white"
              style={{
                textShadow: '0 0 20px rgba(255,255,255,0.22), 0 0 6px rgba(255,255,255,0.1)',
              }}
            >
              {displayName}
            </h3>
            <span className="inline-flex shrink-0 items-center gap-1 rounded-lg border border-amber-500/25 bg-amber-950/40 px-2 py-1 font-poppins text-[10px] font-semibold text-amber-200">
              <XatsPriceValue className="text-[10px] text-amber-200">{ace.storePriceXats}</XatsPriceValue>
              <span className="text-amber-200/80">xats</span>
              <span className="text-amber-200/60">/ {ace.subscriptionDays}d</span>
            </span>
          </div>

          <p className="mb-3 flex-1 font-inter text-sm leading-relaxed text-blue-50/80 line-clamp-2">
            {stripHtmlToText(ace.shortDescription)}
          </p>

          <AceBadgeRow badges={badges} variant="dark" className="mb-4" />

          <div
            className={cn(
              'mt-auto inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5',
              'bg-white/[0.07] ring-1 ring-white/[0.12]',
              'font-poppins text-xs font-semibold text-white',
              'transition-all duration-200 group-hover:bg-white/[0.12] group-hover:ring-amber-400/30 group-hover:text-amber-100'
            )}
          >
            <span>Ver Ace</span>
            <ArrowRight className="h-3.5 w-3.5 text-white/40 transition-colors duration-200 group-hover:text-amber-300/70" />
          </div>
        </div>
      </PowerDarkCardShell>
    </Link>
  );
}
